/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { mq } from '../../../helpers'
import { Header1 } from '../../../styles/Components'
import Button from '../../Button'

const HeroWrapper = styled.div`
  width: 100%;
  max-width: 1600px;

  height: 594px;
  aspect-ratio: 16/9;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;

  ${mq('min', '769px')} {
    height: auto;
  }

  img {
    width: 90%;
    height: auto;
    z-index: 500;

    ${mq('min', '769px')} {
      width: 70%;
      height: auto;
    }
  }

  h1 {
    z-index: 555;
  }

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;

    object-fit: cover;
    height: 100%;

    ${mq('min', '769px')} {
      object-fit: fill;
      height: auto;
    }
  }
`

const HeroSection = () => {
  const videoPlayer = useRef(null)
  const [shouldLoadVideo, setShouldLoadVideo] = useState(true)

  // Network-aware: skip the video entirely on slow connections / data-saver.
  useEffect(() => {
    if ('connection' in navigator || 'mozConnection' in navigator || 'webkitConnection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
      if (connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g' || connection.saveData) {
        setShouldLoadVideo(false)
      }
    }
  }, [])

  // Defer the video fetch until after first paint, then load ONE correct source.
  // The preloaded poster (fetchPriority=high, pages/index.js <Head>) is the LCP and paints
  // instantly; the video streams in afterward, so it never competes for LCP
  // bandwidth (fixes H1). Reading window.innerWidth once — instead of
  // useIsDesktop(), which is false on first render — picks the right source a
  // single time, so desktop no longer fetches mobile-then-desktop (fixes H2).
  useEffect(() => {
    if (!videoPlayer.current || !shouldLoadVideo) {
      return undefined
    }

    const videoElement = videoPlayer.current

    const start = () => {
      const isDesktop = window.innerWidth >= 769
      const base = 'https://images.glamsquad.com/client/www/'
      const webmSrc = isDesktop ? `${base}hero-video-desktop.webm` : `${base}hero-video-mobile.webm`
      const mp4Src = isDesktop ? `${base}hero-video-desktop.mp4` : `${base}hero-video-mobile.mp4`

      videoElement.innerHTML = `
        <source src="${webmSrc}" type="video/webm">
        <source src="${mp4Src}" type="video/mp4">
      `
      videoElement.load()
      // The autoPlay attribute starts playback once buffered; play() covers
      // browsers that don't auto-start after a late load(). Muted, so allowed.
      const played = videoElement.play()
      if (played && typeof played.catch === 'function') {
        played.catch(() => {})
      }
    }

    // Push the fetch past the critical-render window. requestIdleCallback is
    // absent in some browsers (and jsdom) — fall back to a short timeout.
    const ric = typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback : null
    const id = ric ? ric(start, { timeout: 2000 }) : setTimeout(start, 300)

    return () => {
      if (ric && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(id)
      } else {
        clearTimeout(id)
      }
    }
  }, [shouldLoadVideo])

  return (
    <HeroWrapper>
      {shouldLoadVideo ? (
        <video
          ref={videoPlayer}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          poster='/home/hero-video-first-frame.jpg'
        />
      ) : (
        <img
          src='/home/hero-video-first-frame.jpg'
          alt='Glamsquad Hero'
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      )}
      <img src='/home/new-logo.svg' width="351" height="48.195" alt='Glamsquad Logo' className='gs-logo' fetchPriority="high" />
      <Header1 white margin='28px 0'>
        BEAUTY ON CALL
      </Header1>
      <Button large hover path='book'>
        Book Now
      </Button>
    </HeroWrapper>
  )
}

export default HeroSection
